// Mobile Menu — 低频入口 + PC版限定功能提示。
// Mobile Mail v1.4 (2026-09-14):
//   - Bottom Nav 已经有 Top / 分析 / Blog / Mail,所以 Menu 只放剩下入口
//   - "スマートフォンで利用できます" section:
//     サイト管理 / Googleビジネス / Instagram Management(3 个)
//   - "PC版でご利用ください" section:
//     AIブログ生成 / メディア / チャットボット / 設定 / ユーザー管理
//     点 each → sub-view(タイトル + 文 + メニューに戻る)
//   - 不调 API / 不写功能 / 不加权限
// 设计:复用 mobile-menu-grid + mobile-menu-item,5 个 PC 项 3 列宫格 2 行。

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Bot,
  Building2,
  Globe,
  Image as ImageIcon,
  Instagram,
  LogOut,
  Settings,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../../auth/AuthProvider";
import { MobileCard } from "../components/MobileCard";
import { MobileHeader } from "../components/MobileHeader";

type MobileItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  bg: string;
  color: string;
};

type PcOnlyItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  bg: string;
  color: string;
  // sub-view
  title: string;
  body: string[]; // each string → <p class="mobile-menu-pc-body">
};

// スマホで使える 3 件(Bottom Nav と被らない)
const mobileItems: MobileItem[] = [
  { to: "/mobile/site", label: "サイト管理", icon: Globe, bg: "#eff6ff", color: "#2563eb" },
  {
    to: "/mobile/google-business",
    label: "Googleビジネス",
    icon: Building2,
    bg: "#fff7ed",
    color: "#ea580c",
  },
  {
    to: "/mobile/instagram",
    label: "Instagram Management",
    icon: Instagram,
    bg: "#fdf2f8",
    color: "#ec4899",
  },
];

// PC 限定 5 件(クリックで sub-view 表示,不调 API)
const pcOnlyItems: PcOnlyItem[] = [
  {
    id: "ai-blog",
    label: "AIブログ生成",
    icon: Sparkles,
    bg: "#f5f3ff",
    color: "#7c3aed",
    title: "AIブログ生成",
    body: [
      "この機能はPC版でご利用ください。",
      "記事生成・編集・SEO設定などの操作は、PC版でのご利用を推奨しています。",
    ],
  },
  {
    id: "media",
    label: "メディア",
    icon: ImageIcon,
    bg: "#f1f5f9",
    color: "#64748b",
    title: "メディア",
    body: [
      "この機能はPC版でご利用ください。",
      "画像・ファイルの管理は、PC版でのご利用を推奨しています。",
    ],
  },
  {
    id: "chatbot",
    label: "チャットボット",
    icon: Bot,
    bg: "#d1fae5",
    color: "#10b981",
    title: "チャットボット",
    body: [
      "この機能はPC版でご利用ください。",
      "チャットボットの設定・管理は、PC版でのご利用を推奨しています。",
    ],
  },
  {
    id: "settings",
    label: "設定",
    icon: Settings,
    bg: "#fef3c7",
    color: "#f59e0b",
    title: "設定",
    body: [
      "この機能はPC版でご利用ください。",
      "システム設定の変更は、PC版での操作を推奨しています。",
    ],
  },
  {
    id: "users",
    label: "ユーザー管理",
    icon: Users,
    bg: "#fee2e2",
    color: "#ef4444",
    title: "ユーザー管理",
    body: [
      "この機能はPC版でご利用ください。",
      "ユーザー権限やメンバー管理は、PC版での操作を推奨しています。",
    ],
  },
];

const PcSubView = ({
  item,
  onBack,
}: {
  item: PcOnlyItem;
  onBack: () => void;
}) => {
  const Icon = item.icon;
  return (
    <>
      <MobileHeader title={item.title} />
      <main className="mobile-content">
        <MobileCard>
          <div className="mobile-menu-pc-message">
            <div
              className="mobile-menu-pc-icon"
              style={{ background: item.bg, color: item.color }}
              aria-hidden="true"
            >
              <Icon size={26} strokeWidth={1.8} />
            </div>
            <h2 className="mobile-menu-pc-title">{item.title}</h2>
            {item.body.map((text, i) => (
              <p key={i} className="mobile-menu-pc-body">
                {text}
              </p>
            ))}
            <button
              type="button"
              className="mobile-btn mobile-btn-primary mobile-btn-block"
              onClick={onBack}
              style={{ marginTop: 12 }}
            >
              <ArrowLeft size={16} strokeWidth={2} />
              メニューに戻る
            </button>
          </div>
        </MobileCard>
      </main>
    </>
  );
};

export const MobileMenuPage = () => {
  const [pcViewId, setPcViewId] = useState<string | null>(null);
  const { logout } = useAuth();

  const pcViewItem = pcViewId
    ? pcOnlyItems.find((i) => i.id === pcViewId)
    : null;

  const handleLogout = async () => {
    await logout();
    // logout() 内で state が null になり、ProtectedRoute が /login へ redirect
  };

  if (pcViewItem) {
    return <PcSubView item={pcViewItem} onBack={() => setPcViewId(null)} />;
  }

  return (
    <>
      <MobileHeader title="メニュー" />
      <main className="mobile-content">
        <div>
          <h3 className="mobile-menu-section-title">
            スマートフォンで利用できます
          </h3>
          <MobileCard className="mobile-card--no-padding">
            <div className="mobile-menu-grid">
              {mobileItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="mobile-menu-item"
                  >
                    <div
                      className="mobile-menu-item-icon"
                      style={{ background: item.bg, color: item.color }}
                    >
                      <Icon size={22} strokeWidth={1.8} />
                    </div>
                    <span className="mobile-menu-item-label">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </MobileCard>
        </div>

        <div>
          <h3 className="mobile-menu-section-title">
            PC版でご利用ください
          </h3>
          <MobileCard className="mobile-card--no-padding">
            <div className="mobile-menu-grid">
              {pcOnlyItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="mobile-menu-item mobile-menu-item--pc"
                    onClick={() => setPcViewId(item.id)}
                  >
                    <div
                      className="mobile-menu-item-icon"
                      style={{ background: item.bg, color: item.color }}
                    >
                      <Icon size={22} strokeWidth={1.8} />
                    </div>
                    <span className="mobile-menu-item-label">{item.label}</span>
                    <span className="mobile-menu-item-pc-tag">PC</span>
                  </button>
                );
              })}
            </div>
          </MobileCard>
        </div>

        {/* アカウント — Logout */}
        <button
          type="button"
          className="mobile-menu-logout"
          onClick={handleLogout}
        >
          <LogOut size={16} strokeWidth={2} />
          <span>ログアウト</span>
        </button>
      </main>
    </>
  );
};